import * as React from 'react'
import cx from 'classnames'
import {
  useCombobox,
  UseComboboxReturnValue,
  UseComboboxState,
} from 'downshift'
import { Observable } from 'rxjs'
import { useObservableFocus, useFocusTracking } from 'hooks/focus-hooks'
import { IconButton } from 'components/IconButton'
import Chip from 'components/Chip'
import Icon from 'components/Icon'
import Spinner from 'components/Spinner'
import SearchResultItem from './SearchResultItem'
import styles from './CustomSelect.sass'
import SearchResults from './SearchResults'
import { isNotNil } from 'utils/object-util'
import { ControlStatus } from '../ControlStatus'
import Member from 'iam/models/Member'
import { useFloating, autoUpdate, shift } from '@floating-ui/react'

// Re-export the hooks.
export * from './item-source-hook'
export * from './infinite-scroll-provider-hook'
export { ItemSource } from './CustomSelectStateObservable'

/**
 * Props for the CustomSelect component.
 */
export interface CustomSelectProps<
  V, // Value type
  M extends true | false, // Whether multi is on or not.
  VT = M extends false ? V : V[], // The value type, conditional based on multi
> extends Partial<CustomSelectState<V>> {
  /**
   * The current value. If `multi`, an array should be specified.
   */
  value: VT | undefined
  /**
   * Whether this is a multi select.
   */
  multi?: M
  /**
   * If `true`, the menu will stay open when a selection is made.
   */
  persist?: boolean
  /**
   * Whether the component is disabled.
   */
  disabled?: boolean
  /**
   * Control status.
   */
  status?: ControlStatus
  /**
   * Placeholder text to use for the search field when no selection exists.
   */
  placeholder?: string
  /**
   * Indicates whether search results should be show above or below the input.
   */
  searchResultsPlacement?: 'above' | 'below'
  /**
   * The search field value.
   */
  inputValue: string

  /**
   * Callback for triggering loading more.
   */
  onLoadMore(): unknown

  /**
   * Callback for changing the search field value.
   * @param value
   */
  onInputValueChange(value: string): unknown

  /**
   * If provided, triggers input focus when a value is emitted.
   */
  focus$?: Observable<unknown>

  /**
   * Focus event.
   */
  onFocus?: React.FocusEventHandler

  /**
   * Blur event.
   */
  onBlur?: React.FocusEventHandler

  /**
   * Change handler. If single select, `null` means clear. If multi, an empty array will
   * mean clear.
   * @param newValue
   */
  onChange: (newValue: M extends false ? VT | undefined : VT) => unknown

  /**
   * If `undefined` (default), renders an arrow icon.
   * If `null`, renders no icon.
   * If specified, renders what is given.
   */
  icon?: null | undefined | React.ReactNode

  /**
   * Used when not providing a `renderItem` and/or `renderSearchItem` impl.
   */
  itemToString?: (item: V) => string

  /**
   * If specified, used for comparing whether items are equal.
   * Used for filtering out selected items from search result.
   */
  areItemsEqual?: (
    left: V | undefined | null,
    right: V | undefined | null,
  ) => boolean

  /**
   * If specified, used for rendering selected items when `multi` is `true`.
   */
  renderItem?(itemProps: RenderItemProps<V>): React.ReactElement

  /**
   * If specified, used for rendering the search items.
   */
  renderSearchItem?(itemProps: RenderSearchItemProps<V>): React.ReactElement

  /**
   * Called when `Enter` is downed while not trying to select an item.
   *
   * @param e
   */
  onEnterDown?(e: React.KeyboardEvent): unknown

  /**
   * Whether the status of the selector passes validation.
   */
  isNotValid?: boolean

  /**
   * Test selector.
   */
  ['data-test']?: string
}

/**
 * Props for rendering an item in a multi select.
 */
export interface RenderItemProps<V> {
  item: V
  disabled: boolean
  removeItem: () => unknown
  itemToString: (item: V) => string
  focusInputField: () => unknown
  toggleMenu: () => unknown
  ['data-test']?: string
}

/**
 * Props for rendering a search result item.
 */
export interface RenderSearchItemProps<V>
  extends React.HTMLAttributes<HTMLElement> {
  item: V
  selected: boolean
  highlighted: boolean
  itemToString: (v: V) => string
}

/**
 * State shape.
 */
export interface CustomSelectState<V> {
  /**
   * The search results.
   */
  searchResults: V[]
  /**
   * Whether we are loading.
   */
  isLoading: boolean
  /**
   * Whether we have reached the end of stream.
   */
  isEoS: boolean
}

/**
 * Custom Select State helper.
 */
export const CustomSelectState = {
  initial: {
    isLoading: false,
    isEoS: true,
    searchResults: [],
  } as CustomSelectState<any>,
}

/**
 * Custom select component.
 */
export function CustomSelect<V, M extends boolean>({
  value,
  disabled,
  status,
  multi,
  onChange,
  placeholder,
  icon,
  persist,
  onEnterDown,
  onBlur,
  onFocus,
  focus$,
  isEoS,
  isLoading,
  isNotValid,
  'data-test': dataTest = 'custom-select',
  searchResults = [],
  inputValue,
  onInputValueChange,
  onLoadMore,
  itemToString: actualItemToString = String,
  areItemsEqual = defaultAreItemsEqual,
  renderItem = multi ? defaultRenderItem : defaultRenderSingleItem,
  renderSearchItem = defaultRenderSearchItem,
  searchResultsPlacement = 'below',
}: CustomSelectProps<V, M>) {
  const inputRef = React.useRef<HTMLInputElement | null>(null)

  const {
    x: left,
    y: top,
    refs,
    strategy,
    update,
  } = useFloating({
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    middleware: [shift()],
  })

  const itemToString = React.useCallback(
    (item: V | undefined) => (isNotNil(item) ? actualItemToString(item) : ''),
    [actualItemToString],
  )

  // Whether we currently have anything selected.
  const hasSelection = multi
    ? (value as V[])?.length > 0
    : !(value === null || value === undefined)
  const focusInputField = React.useCallback(() => inputRef.current?.focus(), [])

  useObservableFocus(focus$, focusInputField)

  // Tracks focus of the input field.
  const [focusProps, isFocused] = useFocusTracking({
    onBlur,
    onFocus,
  })

  // When using multi select, we don't want to display
  // items already selected.
  const filteredSearchResults = React.useMemo(() => {
    if (multi) {
      return searchResults.filter(
        (left) => !(value as V[]).some((right) => areItemsEqual(left, right)),
      )
    }
    return searchResults
  }, [searchResults, value, multi, areItemsEqual])

  // Can be used to clear the selection.
  const removeItem = React.useCallback(
    (item: V) => {
      if (multi) {
        onChange((value as V[]).filter((v) => v !== item) as any)
      } else {
        onChange(undefined!)
      }
      focusInputField()
    },
    [onChange, value, focusInputField, multi],
  )

  // Change handler.
  // The reason for the `as any` is TS can't refine the type
  // here. However, we do get a type-safe API and that's what matters.
  const change = React.useCallback(
    (e: Partial<UseComboboxState<V | null>>) => {
      const item = e.selectedItem
      if (multi) {
        if (item === null) {
          onChange([] as any)
        } else {
          if (!(value as V[]).some((v) => areItemsEqual(v, item))) {
            onChange([...(value as any), item] as any)
          }
        }
      } else {
        if (areItemsEqual(value as V | undefined, item)) {
          return
        }

        onChange(item as any)
      }
    },
    [onChange, value, multi, areItemsEqual, persist],
  )

  // Focus the input when clicking the input box.
  const onInputBoxClick = React.useCallback((e: React.MouseEvent) => {
    if (e.currentTarget === e.target) {
      focusInputField()
    }
  }, [])

  const onStateChange = React.useCallback(
    (e: Partial<UseComboboxState<V>>) => {
      if (e.selectedItem) {
        onInputValueChange('')
        return
      }

      switch ((e as any).type) {
        case useCombobox.stateChangeTypes.FunctionCloseMenu:
        case useCombobox.stateChangeTypes.InputBlur:
          onInputValueChange('')
          break
      }
    },
    [onInputValueChange],
  )

  const onDownshiftInputValueChange = React.useCallback(
    (e: Partial<UseComboboxState<V>>) => {
      if (e.inputValue === null || e.inputValue === undefined) {
        return
      }

      // If single select, and we already have a selection, prevent further
      // input.
      if (!multi && hasSelection) {
        return
      }

      onInputValueChange(e.inputValue)
    },
    [onInputValueChange, multi, hasSelection],
  )

  const downshift: UseComboboxReturnValue<V> = useCombobox<V>({
    onStateChange,
    inputValue,
    items: filteredSearchResults,
    onInputValueChange: onDownshiftInputValueChange,
    selectedItem: null,
    onSelectedItemChange: change,
    itemToString: emptyStringGetter,
    // Ensures that the menu stays open when selecting items in a multi select
    stateReducer(_, actionAndChanges) {
      const { changes, type } = actionAndChanges
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          return persist
            ? { ...changes, isOpen: true }
            : { ...changes, isOpen: false }

        default:
          return changes
      }
    },
  })

  // Tell floating ui when to update position.
  React.useEffect(() => {
    if (downshift.isOpen && refs.reference.current && refs.floating.current) {
      return autoUpdate(refs.reference.current, refs.floating.current, update, {
        animationFrame: true,
      })
    }
  }, [downshift.isOpen, update, refs.floating, refs.reference])

  // Various nice shortcuts.
  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent) => {
      // Deletes the last item if backspacing an empty input field.
      if (
        event.key === 'Backspace' &&
        !(event.target as HTMLInputElement).value
      ) {
        if (multi) {
          const arr = value as V[]
          if (arr.length > 0) {
            onChange(arr.slice(0, arr.length - 1) as any)
          }
        } else {
          onChange(undefined!)
        }
      }

      // Don't clear selection on Esc, clear input value, close menu.
      if (event.key === 'Escape') {
        ;(event.nativeEvent as any).preventDownshiftDefault = true
        onInputValueChange('')
        downshift.closeMenu()
      }

      if (event.key === 'Enter' && downshift.highlightedIndex === null) {
        onEnterDown?.(event)
      }
    },
    [
      value,
      multi,
      onEnterDown,
      onChange,
      searchResults,
      onInputValueChange,
      downshift.highlightedIndex,
      downshift.closeMenu,
    ],
  )

  return (
    <div
      className={cx(styles.root)}
      {...downshift.getMenuProps()}
      data-test={dataTest}
    >
      <div
        ref={refs.setReference}
        className={cx(
          styles.control,
          disabled && styles.disabled,
          status && styles[status],
          isFocused && styles.focused,
          isNotValid && styles.isNotValid,
        )}
      >
        <div
          className={cx(styles.inputBox, multi && styles.multiInputBox)}
          onClick={onInputBoxClick}
        >
          {multi
            ? (value as V[]).map((item, i) => (
                <div
                  key={i}
                  className={styles.selectedItem}
                  onClick={(e) => e.stopPropagation()}
                >
                  {renderItem({
                    item,
                    'data-test': 'selected-item',
                    itemToString,
                    disabled: !!disabled,
                    focusInputField,
                    toggleMenu: downshift.toggleMenu,
                    removeItem: () => (disabled ? undefined : removeItem(item)),
                  })}
                </div>
              ))
            : value !== undefined &&
              value !== null && (
                <div
                  className={styles.selectedItem}
                  onClick={(e) => e.stopPropagation()}
                >
                  {renderItem({
                    item: value as V,
                    itemToString,
                    'data-test': 'selected-item',
                    disabled: !!disabled,
                    toggleMenu: downshift.toggleMenu,
                    focusInputField,
                    removeItem: () =>
                      disabled ? undefined : removeItem(value as V),
                  })}
                </div>
              )}
          <div className={styles.inputContainer}>
            <input
              type="text"
              className={styles.input}
              autoCapitalize="off"
              autoCorrect="off"
              data-test={`${dataTest}-input`}
              disabled={disabled}
              {...downshift.getInputProps({
                onFocus: focusProps.onFocus,
                onBlur: focusProps.onBlur,
                onKeyDown: handleKeyDown,
                ref: inputRef,
              })}
              placeholder={hasSelection ? '' : placeholder}
              style={{
                width: 2 + inputValue.length * 7,
              }}
            />
          </div>
        </div>
        <div className={styles.side}>
          <div
            onClick={focusInputField}
            className={cx(
              styles.loading,
              isLoading && downshift.isOpen && styles.showLoading,
            )}
          >
            <Spinner size={16} thickness={2} />
          </div>

          {icon !== null && (
            <div className={styles.actions}>
              <IconButton
                dataTest={`${dataTest}-toggle-dropdown-button`}
                inheritColor
                disabled={disabled}
                onClick={() => {
                  if (downshift.isOpen) {
                    downshift.closeMenu()
                  } else {
                    downshift.openMenu()
                  }
                }}
              >
                {icon || <Icon rotate={90} name="arrow" />}
              </IconButton>
            </div>
          )}
        </div>
      </div>
      <div
        className={styles.resultsContainer}
        {...downshift.getMenuProps({
          ref: refs.floating as React.MutableRefObject<HTMLDivElement>,
          style: {
            position: strategy,
            maxWidth: refs.reference.current?.getBoundingClientRect().width,
            maxHeight: refs.reference.current?.getBoundingClientRect().height,
            top: top ?? 0,
            left: left ?? 0,
          },
        })}
      >
        {downshift.isOpen && !disabled && (
          <SearchResults
            placement={searchResultsPlacement}
            isLoading={isLoading || false}
            onLoadMore={onLoadMore}
            areItemsEqual={areItemsEqual as any} // type does not flow through correctly
            value={value}
            filteredSearchResults={filteredSearchResults}
            searchResults={searchResults}
            itemToString={itemToString}
            renderSearchItem={renderSearchItem}
            highlightedIndex={downshift.highlightedIndex}
            getItemProps={downshift.getItemProps}
            isEoS={isEoS || false}
            multi={Boolean(multi)}
          />
        )}
      </div>
    </div>
  )
}

/**
 * Default search result item renderer.
 */
export function defaultRenderSearchItem<V>({
  item,
  itemToString,
  ...props
}: RenderSearchItemProps<V>) {
  const itemAsString = itemToString(item)
  return <SearchResultItem {...props}>{itemAsString}</SearchResultItem>
}

/**
 * Default multi-select item renderer.
 */
export function defaultRenderItem<V>(
  props: React.Attributes & RenderItemProps<V>,
) {
  return <MultiItemSelectedDefault {...props} />
}

/**
 * Props for the `SingleItemSelectedDefault` component.
 */
export interface MultiItemSelectedDefaultProps<V> extends RenderItemProps<V> {
  /**
   * An icon or something to render next to the text.
   */
  visual?: React.ReactNode
}

/**
 * The above as a component since we want to use hooks.
 */
export function MultiItemSelectedDefault<V>({
  item,
  disabled,
  itemToString,
  visual,
  removeItem,
  'data-test': dataTest,
}: MultiItemSelectedDefaultProps<V>) {
  const itemAsString = itemToString(item)
  return (
    <div className={styles.defaultMultiSelectedItem}>
      <Chip
        onRemoveClick={disabled ? undefined : removeItem}
        data-test={dataTest}
      >
        {visual && (
          <span className={styles.defaultSingleSelectedItemVisual}>
            {visual}
          </span>
        )}
        <span className={styles.defaultSingleSelectedMutliItemText}>
          {itemAsString}
        </span>
      </Chip>
    </div>
  )
}

/**
 * Default single-select item renderer.
 * Designed to look like an input field.
 */
export function defaultRenderSingleItem<V>(
  props: React.Attributes & RenderItemProps<V>,
) {
  return <SingleItemSelectedDefault {...props} />
}

/**
 * Props for the `SingleItemSelectedDefault` component.
 */
export interface SingleItemSelectedDefaultProps<V> extends RenderItemProps<V> {
  /**
   * An icon or something to render next to the text.
   */
  visual?: React.ReactNode
}

/**
 * The above as a component since we want to use hooks.
 */
export function SingleItemSelectedDefault<V>({
  item,
  disabled,
  itemToString,
  focusInputField,
  toggleMenu,
  visual,
  'data-test': dataTest,
}: SingleItemSelectedDefaultProps<V>) {
  const onClick = React.useCallback(() => {
    toggleMenu()
    focusInputField()
  }, [focusInputField, toggleMenu])
  const itemAsString = itemToString(item)
  return (
    <div
      onClick={disabled ? undefined : onClick}
      className={styles.defaultSingleSelectedItem}
      data-test={dataTest}
    >
      {visual && (
        <span className={styles.defaultSingleSelectedItemVisual}>{visual}</span>
      )}
      <span className={styles.defaultSingleSelectedItemText}>
        {itemAsString}
      </span>
    </div>
  )
}

/**
 * Default implementation of `areItemsEqual`.
 */
function defaultAreItemsEqual(left: unknown, right: unknown) {
  return left === right
}

/**
 * Stable function for getting an empty string.
 */
function emptyStringGetter() {
  return ''
}

/**
 * Provides member's info for dropdown menus.
 */
export function formatMemberOption(member: Member) {
  return `${member.displayName} (email: ${member.email}, id: ${member.user.publicId})`
}
